import React, { useState } from 'react';
import { Cpu, X, TrendingDown, BarChart3, FileText, GitCompare, Loader2, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';

const AI_FEATURES = [
  { id: 'portfolio-risk', icon: TrendingDown, label: 'Portfolio Risk', desc: 'Analyze overall portfolio risk, concentration, and correlation', endpoint: '/api/ai/portfolio-risk', color: '#ff4757' },
  { id: 'weekly-report', icon: FileText, label: 'Weekly Report', desc: 'AI-generated weekly performance summary with action items', endpoint: '/api/ai/weekly-report', color: '#00ff88' },
  { id: 'ea-comparison', icon: GitCompare, label: 'EA Optimization', desc: 'Which EAs to scale up, maintain, or shut down', endpoint: '/api/ai/ea-comparison', color: '#a855f7' },
  { id: 'drawdown-analysis', icon: AlertTriangle, label: 'Drawdown Intel', desc: 'What caused the drawdown and how to recover', endpoint: '/api/ai/drawdown-analysis', color: '#f59e0b' },
];

export default function AIHub({ isOpen, onClose }) {
  const [activeFeature, setActiveFeature] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const runFeature = async (feature) => {
    setActiveFeature(feature.id);
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await apiFetch(feature.endpoint, {
        method: 'POST',
        body: {},
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || `Error ${res.status}`);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError('Network error. Check connection.');
    } finally {
      setLoading(false);
    }
  };

  const renderAnalysis = (text) => {
    if (!text) return null;
    const sections = text.split(/### /);
    return sections.map((section, i) => {
      if (!section.trim()) return null;
      const lines = section.split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      return (
        <div key={i} className="mb-4">
          <h4 className="text-xs font-bold font-display uppercase tracking-wider mb-1.5 text-accent-green">{title}</h4>
          <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
            {content.split('\n').map((line, j) => {
              if (line.startsWith('- ')) return <p key={j} className="ml-3 mb-1 text-gray-400">• {line.slice(2)}</p>;
              if (line.startsWith('[') && line.endsWith(']')) return <p key={j} className="text-lg font-bold font-display text-white">{line.replace(/[\[\]]/g, '')}</p>;
              return line ? <p key={j} className="mb-1">{line}</p> : null;
            })}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-blue/20 border border-accent-purple/30 flex items-center justify-center">
              <Cpu size={20} className="text-accent-purple" />
            </div>
            <div>
              <h2 className="text-base font-bold font-display text-gray-100">AI Intelligence Hub</h2>
              <p className="text-[11px] text-gray-500">Hedge fund-grade analytics powered by AI</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-dark-hover transition">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Feature list */}
          <div className="w-[260px] border-r border-dark-border p-4 space-y-2 overflow-y-auto">
            {AI_FEATURES.map(feature => {
              const Icon = feature.icon;
              const isActive = activeFeature === feature.id;
              return (
                <button
                  key={feature.id}
                  onClick={() => runFeature(feature)}
                  className={`w-full text-left p-3 rounded-xl transition-all ${isActive ? 'bg-white/[0.05] border border-white/10' : 'hover:bg-white/[0.03] border border-transparent'}`}
                >
                  <div className="flex items-center gap-2.5 mb-1">
                    <Icon size={15} style={{ color: feature.color }} />
                    <span className="text-sm font-semibold text-gray-200 font-display">{feature.label}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 leading-relaxed">{feature.desc}</p>
                </button>
              );
            })}

            <div className="pt-3 mt-3 border-t border-dark-border">
              <p className="text-[9px] text-gray-600 leading-relaxed">
                AI analysis uses your configured provider. Each query costs ~$0.001-0.01 depending on model.
              </p>
            </div>
          </div>

          {/* Right: Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {!activeFeature && !loading && (
              <div className="text-center py-16">
                <Cpu size={40} className="text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">Select an analysis type from the left panel</p>
                <p className="text-gray-600 text-xs mt-1">AI will analyze your full trading data</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-16">
                <Loader2 size={32} className="text-accent-purple animate-spin mx-auto mb-4" />
                <p className="text-gray-400 text-sm font-display">Analyzing your portfolio...</p>
                <p className="text-gray-600 text-xs mt-1">This may take 15-30 seconds</p>
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <AlertTriangle size={24} className="text-accent-red mx-auto mb-2" />
                <p className="text-accent-red text-sm mb-2">Analysis Failed</p>
                <p className="text-gray-500 text-xs">{error}</p>
              </div>
            )}

            {result && !loading && (
              <div>
                <p className="text-[10px] text-gray-600 mb-4">Generated: {new Date(result.generated_at).toLocaleString()}</p>
                {renderAnalysis(result.analysis)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
